import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2, Download, Home } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentStatusPage = () => {
  const { txnId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);

  const fetchPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/${txnId}`);
      setPayment(response.data);

      if (["succeeded", "failed", "cancelled"].includes(response.data.status)) {
        setPolling(false);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du statut", error);
      toast.error("Impossible de récupérer le statut du paiement");
    } finally {
      setLoading(false);
    }
  };

  const simulateSuccess = async () => {
    try {
      await axios.post(`${API}/webhook/simulate-success/${txnId}`);
      toast.success("Paiement simulé comme succès");
      setTimeout(() => fetchPaymentStatus(), 1000);
    } catch (error) {
      toast.error("Erreur lors de la simulation");
    }
  };

  useEffect(() => {
    fetchPaymentStatus();
  }, [txnId]);

  useEffect(() => {
    if (!polling) return;

    const interval = setInterval(() => {
      fetchPaymentStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [polling, txnId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
          <p className="text-lg text-[#5C5C5C]">Chargement...</p>
        </div>
      </div>
    );
  }

  const statusConfig = {
    succeeded: {
      icon: <CheckCircle2 className="w-16 h-16 text-green-600" />,
      title: "Paiement réussi !",
      description: "Votre achat a été confirmé. Vous pouvez maintenant télécharger votre ebook.",
      color: "text-green-600"
    },
    failed: {
      icon: <XCircle className="w-16 h-16 text-red-600" />,
      title: "Paiement échoué",
      description: payment?.error_message || "Le paiement n'a pas pu être traité. Veuillez réessayer.",
      color: "text-red-600"
    },
    cancelled: {
      icon: <XCircle className="w-16 h-16 text-orange-600" />,
      title: "Paiement annulé",
      description: "La transaction a été annulée.",
      color: "text-orange-600"
    },
    pending: {
      icon: <Loader2 className="w-16 h-16 animate-spin text-blue-600" />,
      title: "Paiement en attente",
      description: "Veuillez vérifier votre téléphone pour autoriser le paiement.",
      color: "text-blue-600"
    },
    authorizing: {
      icon: <Loader2 className="w-16 h-16 animate-spin text-blue-600" />,
      title: "Autorisation en cours",
      description: "Veuillez compléter l'autorisation sur votre application mobile money.",
      color: "text-blue-600"
    }
  };

  const config = statusConfig[payment?.status] || statusConfig.pending;

  return (
    <div className="min-h-screen bg-[#F9F6F0] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-xl" data-testid="payment-status-card">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center" data-testid="status-icon">
              {config.icon}
            </div>
            <CardTitle className={`text-3xl ${config.color}`} data-testid="status-title">
              {config.title}
            </CardTitle>
            <CardDescription className="text-base" data-testid="status-description">
              {config.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#5C5C5C]">Transaction ID:</span>
                <span className="font-mono text-[#2C2C2C]" data-testid="transaction-id">
                  {payment?.internal_txn_id?.slice(0, 8)}...
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C5C5C]">Montant:</span>
                <span className="font-semibold text-[#2C2C2C]">{payment?.amount_cdf} CDF</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C5C5C]">Mode de paiement:</span>
                <span className="font-medium text-[#2C2C2C] capitalize">
                  {payment?.provider?.replace("_", " ")}
                </span>
              </div>
            </div>

            {payment?.status === "succeeded" && payment?.download_url && (
              <Button
                size="lg"
                className="w-full cta-button text-white py-6 text-lg rounded-full"
                onClick={() => {
                  window.location.href = `${BACKEND_URL}${payment.download_url}`;
                }}
                data-testid="download-button"
              >
                <Download className="mr-2 h-5 w-5" />
                Télécharger l'ebook
              </Button>
            )}

            {["pending", "authorizing"].includes(payment?.status) && (
              <div className="space-y-3">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full py-6 text-lg rounded-full"
                  onClick={simulateSuccess}
                  data-testid="simulate-success-button"
                >
                  Simuler succès (Dev)
                </Button>
                <p className="text-xs text-center text-[#5C5C5C]">
                  En attente de confirmation... Cela peut prendre quelques instants.
                </p>
              </div>
            )}

            {["failed", "cancelled"].includes(payment?.status) && (
              <Button
                size="lg"
                variant="outline"
                className="w-full py-6 text-lg rounded-full"
                onClick={() => navigate("/checkout")}
                data-testid="retry-button"
              >
                Réessayer
              </Button>
            )}

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate("/")}
              data-testid="home-button"
            >
              <Home className="mr-2 h-4 w-4" />
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentStatusPage;